import { Plus, Trash2 } from "lucide-react";
import { coercePersonalSenses, PERSONAL_PARTS_OF_SPEECH, type PersonalSenseInput } from "@preptrack/contracts";

export interface SenseDraft extends PersonalSenseInput {}

export const emptySenseDraft = (): SenseDraft => ({ partOfSpeech: "n.", definitionZh: "" });

export const nextSenseDraft = (senses: SenseDraft[]): SenseDraft => ({
  partOfSpeech: PERSONAL_PARTS_OF_SPEECH.find(
    (partOfSpeech) => !senses.some((sense) => sense.partOfSpeech === partOfSpeech),
  ) ?? PERSONAL_PARTS_OF_SPEECH[0],
  definitionZh: "",
});

export const completedSenseDrafts = (senses: SenseDraft[]) => senses
  .map((sense) => ({ ...sense, definitionZh: sense.definitionZh.trim() }))
  .filter((sense) => sense.definitionZh.length > 0);

export const senseDraftsFromSenses = (senses: Array<{ partOfSpeech: string | null; definitionZh: string | null }>) =>
  coercePersonalSenses(senses);

export function SenseRowsEditor({
  value,
  onChange,
  idPrefix,
  addLabel = "添加词性",
  onCommit,
}: {
  value: SenseDraft[];
  onChange: (value: SenseDraft[]) => void;
  idPrefix: string;
  addLabel?: string;
  onCommit?: (value: SenseDraft[]) => void;
}) {
  const rows = value.length ? value : [emptySenseDraft()];
  const update = (index: number, patch: Partial<SenseDraft>) => {
    onChange(rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
  };
  const remove = (index: number) => {
    const next = rows.filter((_, rowIndex) => rowIndex !== index);
    onChange(next);
    onCommit?.(next);
  };

  return (
    <div className="sense-rows-editor" onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) onCommit?.(rows);
    }}>
      <div className="sense-rows-editor__labels" aria-hidden="true"><span>词性</span><span>释义</span></div>
      {rows.map((sense, index) => (
        <div className="sense-row-editor" key={`${idPrefix}-${index}`}>
          <label className="sr-only" htmlFor={`${idPrefix}-pos-${index}`}>第 {index + 1} 行词性</label>
          <select
            id={`${idPrefix}-pos-${index}`}
            value={sense.partOfSpeech}
            onChange={(event) => update(index, { partOfSpeech: event.target.value as SenseDraft["partOfSpeech"] })}
          >
            {PERSONAL_PARTS_OF_SPEECH.map((partOfSpeech) => <option
              key={partOfSpeech}
              value={partOfSpeech}
              disabled={rows.some((row, rowIndex) => rowIndex !== index && row.partOfSpeech === partOfSpeech)}
            >{partOfSpeech}</option>)}
          </select>
          <label className="sr-only" htmlFor={`${idPrefix}-definition-${index}`}>第 {index + 1} 行中文释义</label>
          <input
            id={`${idPrefix}-definition-${index}`}
            value={sense.definitionZh}
            onChange={(event) => update(index, { definitionZh: event.target.value })}
            maxLength={600}
            placeholder="同一词性的多个释义用分号分隔"
          />
          <button type="button" onClick={() => remove(index)} disabled={rows.length === 1} aria-label={`删除第 ${index + 1} 行词义`} title="删除这行"><Trash2 /></button>
        </div>
      ))}
      <button className="sense-rows-editor__add" type="button" onClick={() => {
        onChange([...rows, nextSenseDraft(rows)]);
      }}><Plus /> {addLabel}</button>
    </div>
  );
}
